import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart, formatEuro } from "../context/CartContext";

/**
 * Pagina Carrello — /carrello.
 *
 * Riepilogo prodotti nel carrello con:
 *   • modifica quantità (+/-),
 *   • rimozione prodotto,
 *   • riepilogo/totale,
 *   • CTA "Procedi all'acquisto" → /checkout.
 *
 * Architettura pensata per supportare N prodotti dell'ecosistema TIME.
 */
export default function CartPage() {
  const { items, updateQuantity, removeItem, subtotal, totalQuantity } = useCart();
  const navigate = useNavigate();
  const empty = items.length === 0;

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-5xl mx-auto">
      <Link
        to="/prodotti"
        data-testid="cart-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Continua a esplorare
      </Link>

      <div className="mb-10">
        <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/80 mb-3">
          TIME Inside · Carrello
        </div>
        <h1
          data-testid="cart-title"
          className="text-4xl sm:text-5xl font-extralight tracking-tight text-white leading-[1.05]"
        >
          Il tuo carrello.
        </h1>
        {!empty && (
          <p className="mt-3 text-white/50 font-light">
            {totalQuantity} {totalQuantity === 1 ? "articolo" : "articoli"} nel carrello.
          </p>
        )}
      </div>

      {empty ? (
        <EmptyCart />
      ) : (
        <div className="grid md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-8 lg:gap-12 items-start">
          {/* Lista prodotti */}
          <ul data-testid="cart-items" className="flex flex-col gap-4">
            {items.map((item) => (
              <li
                key={item.slug}
                data-testid={`cart-item-${item.slug}`}
                className="flex gap-4 sm:gap-5 p-4 sm:p-5 rounded-2xl bg-white/[0.02] border border-white/5"
              >
                <div className="w-20 sm:w-24 shrink-0 rounded-xl overflow-hidden bg-black/40 border border-white/5">
                  {item.cover ? (
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      draggable="false"
                    />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        data-testid={`cart-item-title-${item.slug}`}
                        className="text-white font-light truncate"
                      >
                        {item.title}
                      </div>
                      {item.subtitle && (
                        <div className="text-[12px] text-white/50 font-light truncate">
                          {item.subtitle}
                        </div>
                      )}
                      {item.format && (
                        <div className="mt-1 text-[10px] tracking-[0.28em] uppercase text-emerald-400/70 font-mono">
                          {item.format}
                        </div>
                      )}
                    </div>
                    <button
                      data-testid={`cart-remove-${item.slug}`}
                      type="button"
                      onClick={() => removeItem(item.slug)}
                      className="p-2 rounded-full text-white/40 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      aria-label={`Rimuovi ${item.title}`}
                      title="Rimuovi dal carrello"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    {/* Quantity stepper */}
                    <div className="inline-flex items-center rounded-full border border-white/10 bg-black/30">
                      <button
                        data-testid={`cart-qty-minus-${item.slug}`}
                        type="button"
                        onClick={() =>
                          updateQuantity(item.slug, Math.max(1, item.quantity - 1))
                        }
                        disabled={item.quantity <= 1}
                        className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                        aria-label="Diminuisci quantità"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span
                        data-testid={`cart-qty-value-${item.slug}`}
                        className="w-8 text-center tabular-nums text-sm text-white font-mono"
                      >
                        {item.quantity}
                      </span>
                      <button
                        data-testid={`cart-qty-plus-${item.slug}`}
                        type="button"
                        onClick={() => updateQuantity(item.slug, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                        aria-label="Aumenta quantità"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="text-right">
                      <div
                        data-testid={`cart-item-total-${item.slug}`}
                        className="text-amber-400 font-mono tabular-nums text-lg"
                        style={{ textShadow: "0 0 14px rgba(212,175,55,0.2)" }}
                      >
                        {formatEuro(item.price * item.quantity)}
                      </div>
                      {item.quantity > 1 && (
                        <div className="text-[10px] text-white/40 font-mono">
                          {formatEuro(item.price)} × {item.quantity}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* Riepilogo ordine */}
          <aside
            data-testid="cart-summary"
            className="rounded-2xl bg-white/[0.02] border border-white/5 p-6 sm:p-7 md:sticky md:top-24"
          >
            <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/70 mb-4">
              Riepilogo ordine
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex items-center justify-between text-white/70 font-light">
                <dt>Subtotale</dt>
                <dd
                  data-testid="cart-subtotal"
                  className="tabular-nums text-white"
                >
                  {formatEuro(subtotal)}
                </dd>
              </div>
              <div className="flex items-center justify-between text-white/50 font-light">
                <dt>Consegna digitale</dt>
                <dd className="tabular-nums">Gratuita</dd>
              </div>
            </dl>
            <div className="my-5 h-px bg-white/10" />
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] tracking-[0.32em] uppercase text-white/50 font-mono">
                Totale
              </span>
              <span
                data-testid="cart-total"
                className="text-2xl sm:text-3xl text-amber-400 font-light tabular-nums"
                style={{ textShadow: "0 0 20px rgba(212,175,55,0.3)" }}
              >
                {formatEuro(subtotal)}
              </span>
            </div>

            <button
              data-testid="cart-checkout-btn"
              type="button"
              onClick={() => navigate("/checkout")}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-emerald-400 text-black hover:bg-emerald-300 transition-colors text-[11px] tracking-[0.28em] uppercase font-medium shadow-[0_10px_40px_-10px_rgba(16,185,129,0.6)]"
            >
              Procedi all'acquisto
              <ArrowRight className="w-4 h-4" />
            </button>

            <p className="mt-4 text-[10px] tracking-[0.24em] uppercase text-white/30 text-center font-mono">
              Pagamento sicuro · Fatturazione italiana
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}

function EmptyCart() {
  return (
    <div
      data-testid="cart-empty"
      className="rounded-2xl bg-white/[0.02] border border-white/5 p-12 text-center"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center text-white/40 mb-5">
        <ShoppingBag className="w-6 h-6" />
      </div>
      <h2 className="text-2xl font-extralight text-white">Il carrello è vuoto.</h2>
      <p className="mt-3 text-white/50 font-light max-w-md mx-auto leading-relaxed">
        Esplora l'ecosistema TIME e aggiungi i prodotti che vuoi portare con te.
      </p>
      <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
        <Link
          to="/prodotti/libri/time-il-libro-del-tempo"
          data-testid="cart-empty-explore-time"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-400 text-black hover:bg-emerald-300 transition-colors text-[11px] tracking-[0.28em] uppercase font-medium"
        >
          <ShoppingBag className="w-4 h-4" />
          Scopri TIME · il Libro del Tempo
        </Link>
        <Link
          to="/prodotti"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-colors text-[11px] tracking-[0.28em] uppercase font-mono"
        >
          Vai a Prodotti
        </Link>
      </div>
    </div>
  );
}
